import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import { resendInvite } from "api/Users";
import { getClients, deleteClient, reactivateClient } from "api/Clients";
import { InviteWrapper } from "./styled";
import { PlaneIcon } from "components/svg";
import { Spinner } from "theme-ui";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import { PlusIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import moment, { min } from "moment-timezone";
import AvatarIcon from "components/svg/icons/AvatarIcon";
import Span from "components/typography/Span";
import { Badge, Box, Flex, Image } from "theme-ui";
import { trackActivity } from "api/ActivityTracker";
import UserContext from "context/User";
import CrownIcon from "components/svg/icons/CrownIcon";

type ClientRow = {
  id?: string;
  name?: string;
  typeName?: string;
  category?: string;
  /**
   * 1 active, 2 inactive — the CLIENT's own state, not the user's.
   * `primaryUser.status` below uses 2 to mean "invite sent", which is a
   * different thing entirely.
   */
  status?: number;
  parent?: { name?: string };
  primaryUser?: { status?: number; id?: string; lastActiveAt?: string };
  user?: { createdAt?: string };
  actions?: any;
};


export default function Clients () {
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);

  const columnHelper = createColumnHelper<ClientRow>();
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const hasTrackedView = useRef(false);
  const [currentQuery, setCurrentQuery] = useState<any>(null);  

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const toast = useToast();

  const resendUserInvite = async (user: any) => {
      const res = await resendInvite({ data: user });
      if (res?.success) {
        toast.add("Invite resent.", "var(--theme-ui-colors-success)");
        setRefreshData(true);
        setTimeout(() => {
          setRefreshData(false);
        }, 100);
      } else {
        toast.add(res?.message ? res.message : "Error resending invite.", "var(--theme-ui-colors-danger)");
      }
    };

  const columns = [  
    columnHelper.accessor((row: any) => row.name, {
      id: "name",
      cell: (info: any) => {
        const userData = info.row.original?.primaryUser?.data || [];
        const userLogo = userData.find((d: any) => d.key === "logo")?.value;
        return (
          <Flex sx={{ gap: "1rem", alignItems: "center", cursor:"pointer" }} onClick={() => navigate(`/clients/${info.row.original.id}`)}>
            {userLogo ? (
              <Image width="40px" height="40px" src={userLogo} sx={{borderRadius:"6px"}}/>
            ) : (
              <AvatarIcon fill={"#BFBFBF"} width={"40px"} />
            )}
            <Box>
              <Span color="#000" fontWeight="500">{info.getValue()}</Span>
              <Span color="var(--theme-ui-colors-base_500)" display="block">{info.row.original?.contactEmail}</Span>         
            </Box>
            {(info.row.original?.companyAdmin || info.row.original?.excludeFromReporting) && (
              <Flex sx={{ marginLeft: "auto", gap: "6px", alignItems: "center", flexShrink: 0 }}>
                {/* Item 24 - an excluded client must never be a mystery.
                    If the flag is only visible on the edit page, someone
                    will one day ask why this client is missing from a
                    report and have no way to find out. Neutral grey on
                    purpose: it is a fact about reporting, not a warning
                    about the client. */}
                {info.row.original?.excludeFromReporting && (
                  <Badge
                    as="span"
                    variant="ltgrey"
                    title={info.row.original?.exclusionReason || "Excluded from reports and metrics"}
                  >
                    <span>Not in reports</span>
                  </Badge>
                )}
                {info.row.original?.companyAdmin && (
                  <Badge as="span" sx={{ gap: "10px" }} variant="caution">
                    <span>Admin</span> <CrownIcon fill={"#CAA11C"} width="14px" />
                  </Badge>
                )}
              </Flex>
            )}
          </Flex>
        );
      },
      header: () => <span>User Info</span>,
      enableSorting: false,
      meta: {
        style: { width: "200px", minWidth: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.typeName, {
      id: "typeName",
      cell: (info: any) => info.getValue(),
      header: () => <span>Client Type</span>,
    }), 
    columnHelper.accessor((row: any) => row?.category, {
      id: "category",
      cell: (info: any) => info.getValue(),
      header: () => <span>Client Category</span>,
    }),
    columnHelper.accessor((row: any) => row?.parent?.name, {
      id: "parent.name",
      cell: (info: any) => info.getValue(),
      header: () => <span>Company</span>,
    }),
    columnHelper.accessor((row) => row.primaryUser?.status, {
      id: "status",
      cell: (info) => {
        const [inviteText, setInviteText] = React.useState("Invite Sent");
        const [isProcessing, setIsProcessing] = React.useState(false);

        /* A DEACTIVATED CLIENT OUTRANKS ITS USER'S INVITE STATE.

           This column reads `primaryUser.status`, where 2 means INVITE
           SENT — a different meaning from `client.status`, where 2 means
           inactive. Showing "Invite Sent" on a deactivated client would
           invite someone to resend an invite to an account that is
           switched off, so the client's own state is checked first.

           Before Session 84 this could not arise: deactivating a client
           soft-deleted it and the row left the list entirely, which is
           the bug this replaces. */
        if (info.row.original?.status === 2) {
          return (
            <Badge as="span" variant="warning">
              Inactive
            </Badge>
          );
        }
    
        const handleMouseEnter = () => {
          if (!isProcessing) setInviteText("Resend Invite");
        };
    
        const handleMouseLeave = () => {
          if (!isProcessing) setInviteText("Invite Sent");
        };
    
        const handleClick = async () => {
          setIsProcessing(true);
          try {
            await resendUserInvite(info.row.original.primaryUser);
          } catch (error: any) {
            console.error("Failed to resend invite:", error);
          } finally {
            setIsProcessing(false);
          }
        };
    
        switch (info.getValue()) {
          case 1:
            return <Badge as="span" variant="success">Active</Badge>;
          case 2:
            return (
              <InviteWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>
                <Badge as="span" sx={{ gap: "10px" }} variant="info">
                  <span>{inviteText}</span>
                  {!isProcessing ? <PlaneIcon fill="#104E9A" width="14px" /> : <Spinner sx={{ color: "#104E9A" }} size={14} />}
                </Badge>
              </InviteWrapper>
            );
          default:
            return <Badge as="span" variant="success">Active</Badge>;
        }
      },
      header: () => <span>Status</span>,
      meta: {
        style: { width: "100px", minWidth: "100px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.primaryUser?.lastActiveAt, {
      id: "primaryUser.lastActiveAt",
      cell: (info: any) => info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : "",
      header: () => <span>Last Active At</span>,
    }),
    columnHelper.accessor((row: any) => row?.user?.createdAt, {
      id: "user.createdAt",
      cell: (info: any) => moment(info.getValue()).format("MMM D, YYYY"),
      header: () => <span>Date Added</span>,
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const isInactive = info.row.original?.status === 2;

        /* Deactivate and Reactivate are the same slot, never both. A
           menu offering "Deactivate" on an already-inactive client is
           how you end up clicking it twice and wondering why nothing
           changed. */
        const items = [
          { label: "Details", onClick: () => navigate(`/clients/${info.row.original.id}`) },                    
          { label: "Edit", onClick: () => navigate(`/clients/${info.row.original.id}/edit`) }, 
          isInactive
            ? { label: "Reactivate", onClick: () => submitReactivate(info.row.original) }
            : { label: "Deactivate", onClick: () => submitDelete(info.row.original), style : { color: "var(--theme-ui-colors-danger)"} }
        ];
    
        return (
          <Box>          
            <DropDownMenu
              toggleComponent={
                <EllipsisIcon fill={"#818181"} width="20px" height="20px" />
              }
              items={items}
            />          
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];               

  /**
   * REACTIVATE. No confirmation modal: turning something back ON is not
   * a destructive act, and a dialog guarding it only teaches people to
   * dismiss dialogs.
   */
  const submitReactivate = async (client: any) => {
    const res = await reactivateClient({ data: client });
    if (res?.success) {
      toast.add("Client reactivated.", "var(--theme-ui-colors-success)");
      setRefreshData(true);
      setTimeout(() => { setRefreshData(false) }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error reactivating client.", "var(--theme-ui-colors-danger)");
    }
  };

  const confirmDelete = async (del:any) => { 
    setConfirmSubmit(true);   
    const res = await deleteClient({ data: del });
    if(res?.success){ 
      toast.add("Client deactivated.","var(--theme-ui-colors-success)");
      setRefreshData(true);    
      setTimeout(() => { setRefreshData(false) }, 100);   
    }else{
      toast.add(res?.message ? res.message : "Error deleting client.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const submitDelete = async (del?:any) => {
    setConfirmModal(<BasicModal
      title="Deactivate client?"       
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<>
        <BasicButton
          type="button"    
          scheme="primary"
          outline={true}      
          onClick={() => setConfirmModal(false)} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
        >          
          {"No, cancel"}
        </BasicButton>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete(del)} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
          {"Deactivate"}
        </BasicButton>
      </>}    
    >
      <P>Are you sure you want to deactivate <strong style={{color:"var(--theme-ui-colors-black)"}}>{del?.name}</strong>?</P>
    </BasicModal>);
  }

  useEffect(() => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    
    document.title = process.env.REACT_APP_NAME + " | Clients";
    
    void trackActivity({
      module: "clients",
      action: "view_clients_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      metadata: {
        page: currentQuery?.pageNum || 1,
        limit: currentQuery?.recordsPer || 15,
        filters: currentQuery?.filters || {},
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [currentQuery]); 

  return (
    <>
      <Header></Header>      
      <Page title="Clients" subtitle="Manage your client list.">                
        <TableData 
          api={getClients} 
          columns={columns} 
          refresh={refreshData} 
          actions={[<BasicButton key="1" onClick={() => navigate('/clients/create')} $iconHeight="10px"><PlusIcon fill="#fff"/>Add client</BasicButton>]} 
          title={"All Clients"}
          returnQuery={(query) => setCurrentQuery(query)}
        />
      </Page>      
      {confirmModal}
    </>
  );
}
